import React from 'react';
import { SearchDropdown } from '../../../../../components';
import { Msp } from '../../../../../types';
import { NewExistingSegment, NewExistingValue } from '../../../../../components/shared';

interface MBESelectorProps {
  onMbeBussSelect: (value: number) => void;
  onMbeMainBreakerRatingSelect: (value: number) => void;
  onMbeNewExistingChange: (value: NewExistingValue) => void;
  onMbeMainBreakerNewExistingChange: (value: NewExistingValue) => void;
  selectedMbeBuss?: number;
  selectedMbeMainBreakerRating?: number;
  mbeNewExisting?: NewExistingValue;
  mbeMainBreakerNewExisting?: NewExistingValue;
  mbeBussOptions: Msp[];
  mbeMainBreakerRatingOptions: Msp[];
  isLoading: boolean;
  error?: string;
  isEnabled: boolean;
}

export const MBESelector: React.FC<MBESelectorProps> = ({
  onMbeBussSelect,
  onMbeMainBreakerRatingSelect,
  onMbeNewExistingChange,
  onMbeMainBreakerNewExistingChange,
  selectedMbeBuss,
  selectedMbeMainBreakerRating,
  mbeNewExisting,
  mbeMainBreakerNewExisting,
  mbeBussOptions,
  mbeMainBreakerRatingOptions,
  isLoading,
  error,
  isEnabled
}) => {

  const handleMbeBussSelect = (option: any) => {
    if (!option) {
      // Handle clear action
      onMbeBussSelect(0);
      return;
    }
    const mbeBuss = mbeBussOptions?.find(m => m.id === option.id);
    if (mbeBuss) {
      onMbeBussSelect(parseInt(mbeBuss.value, 10));
    }
  };

  const handleMbeMainBreakerRatingSelect = (option: any) => {
    if (!option) {
      // Handle clear action
      onMbeMainBreakerRatingSelect(0);
      return;
    }
    const mbeMainBreakerRating = mbeMainBreakerRatingOptions?.find(m => m.id === option.id);
    if (mbeMainBreakerRating) {
      onMbeMainBreakerRatingSelect(parseInt(mbeMainBreakerRating.value, 10));
    }
  };

  const mbeBussDropdownOptions = mbeBussOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  const mbeMainBreakerRatingDropdownOptions = mbeMainBreakerRatingOptions?.map(option => ({
    id: option.id,
    label: option.value
  })) || [];

  if (!isEnabled) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 mb-2">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <p className="text-gray-500 text-xs 3xl:text-sm">Select a task to configure MBE details</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* MBE Buss Rating */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">
            MBE Buss Rating
          </label>
          {isEnabled && (
            <NewExistingSegment
              value={mbeNewExisting || null}
              onChange={onMbeNewExistingChange}
              disabled={!isEnabled}
            />
          )}
        </div>
        <SearchDropdown
          options={mbeBussDropdownOptions}
          onSelect={handleMbeBussSelect}
          placeholder="Choose MBE buss rating..."
          isLoading={isLoading}
          error={error}
          className="h-9"
          value={selectedMbeBuss ? { 
            id: mbeBussOptions.find(opt => parseInt(opt.value, 10) === selectedMbeBuss)?.id || '', 
            label: selectedMbeBuss.toString() 
          } : null}
          disabled={!isEnabled}
        />
      </div>

      {/* MBE Main Breaker Rating */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">
            MBE Main Breaker Rating
          </label>
          {isEnabled && (
            <NewExistingSegment
              value={mbeMainBreakerNewExisting || null}
              onChange={onMbeMainBreakerNewExistingChange}
              disabled={!isEnabled}
            />
          )}
        </div>
        <SearchDropdown
          options={mbeMainBreakerRatingDropdownOptions}
          onSelect={handleMbeMainBreakerRatingSelect}
          placeholder="Choose MBE main breaker rating..."
          isLoading={isLoading}
          error={error}
          className="h-9"
          value={selectedMbeMainBreakerRating ? { 
            id: mbeMainBreakerRatingOptions.find(opt => parseInt(opt.value, 10) === selectedMbeMainBreakerRating)?.id || '', 
            label: selectedMbeMainBreakerRating.toString() 
          } : null}
          disabled={!isEnabled}
        />
      </div>
    </div>
  );
};